import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import {
	GetKYCInvestorInstanceRes,
	KycWorkflowSettingResponseDto,
} from '@dealstack/shared/lib/generated';
import { useTranslationCommonForm } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useManageKYCController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/useManageKYCController';
import { useUpdateInvestorDeclarations } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/api/useUpdateInvestorDeclarations';
import {
	declarationsEditFormShape,
	DeclarationsEditFormValues,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/Declarations/DeclarationsEditForm.types';
import {
	declarationsToFormValues,
	formValuesToDeclarationsPayload,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/Declarations/declarationsEditForm.utils';

interface UseDeclarationsEditFormArgs {
	currentInvestor?: GetKYCInvestorInstanceRes;
	kycSetting?: KycWorkflowSettingResponseDto;
	onSaved: () => void;
}

export const useDeclarationsEditForm = ({
	currentInvestor,
	kycSetting,
	onSaved,
}: UseDeclarationsEditFormArgs) => {
	const tCommonForm = useTranslationCommonForm();
	const [searchParams] = useSearchParams();
	const kycWorkflowId = searchParams.get('kycWorkflowId') ?? undefined;

	const { getKYCDataRequest } = useManageKYCController(currentInvestor?.id, kycWorkflowId);
	const { updateDeclarations, mutation } = useUpdateInvestorDeclarations(getKYCDataRequest);

	const isBeneficialOwnershipRequired = kycSetting?.isBeneficialOwnershipRequired ?? true;
	const isSourceOfFundsRequired = kycSetting?.isSourceOfFundsRequired ?? true;
	const isPepRequired = kycSetting?.isPepRequired ?? true;

	const form = useForm<DeclarationsEditFormValues>({
		mode: 'all',
		reValidateMode: 'onChange',
		resolver: zodResolver(
			declarationsEditFormShape(
				{ isBeneficialOwnershipRequired, isSourceOfFundsRequired, isPepRequired },
				tCommonForm
			)
		),
		defaultValues: declarationsToFormValues(currentInvestor?.declarations),
	});

	const onSubmit = form.handleSubmit(async (values) => {
		await updateDeclarations({
			investorId: currentInvestor?.id ?? '',
			body: formValuesToDeclarationsPayload(values),
			kycWorkflowId,
		});
		onSaved();
	});

	return {
		form,
		onSubmit,
		isSaving: mutation.isPending,
		isBeneficialOwnershipRequired,
		isSourceOfFundsRequired,
		isPepRequired,
	};
};
